import {
  Component,
  ViewChild,
  ViewEncapsulation,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';

import { agGridLocale, DecimalCellRenderer } from 'app/utils/ag-grid.locale';
import { MatDialog } from '@angular/material';
import { PayrollDetailComponent } from '../payroll-detail/payroll-detail.component';
import { PayrollExtraEntry } from 'app/api/payroll.extra.api.service';
import * as _ from 'lodash';
import { PayrollRuleEntry } from 'app/api/payroll.rule.api.service';
import { PayrollEntry, PayrollEntryByName } from 'app/api/payroll.api.service';
import { Store, select } from '@ngrx/store';
import { AppState } from 'app/reducers';
import { PayrollRuleActions } from '../../actions';
import { Observable, Subject } from 'rxjs';
import { PayrollSelectors, PayrollExtraSelectors } from '../../selectors';
import { map, takeUntil } from 'rxjs/operators';
import {
  CompaniesSelectors,
  CompaniesUISelectors,
} from 'app/main/company/selectors';

@Component({
  selector: 'app-ecran-v2',
  templateUrl: './ecran-v2.component.html',
  styleUrls: ['./ecran-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EcranV2Component implements OnInit, OnChanges {
  public gridApi;
  public gridColumnApi;

  public columnDefs;
  public defaultColDef;
  public statusBar;
  public rowBuffer;
  public localeText;
  public modules;

  @Input() years: string[];
  @Input() companyId: string;

  rows$: Observable<PayrollEntryByName[]>;
  extraRows: PayrollExtraEntry[];
  unsubscribeAll: Subject<any>;

  private _search = '';
  @Input()
  set search(value: string) {
    this._search = value;
    if (this.gridApi) {
      this.gridApi.setQuickFilter(value);
    }
  }

  @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;

  constructor(public dialog: MatDialog, protected store: Store<AppState>) {
    this.unsubscribeAll = new Subject();
    this.modules = AllModules;
    this.localeText = agGridLocale.fr;
    this.defaultColDef = {
      enableValue: true,
      sortable: true,
      filter: true,
      resizable: true,
    };
    this.statusBar = {
      statusPanels: [
        {
          statusPanel: 'agTotalRowCountComponent',
          align: 'left',
        },
        {
          statusPanel: 'agAggregationComponent',
        },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agFilteredRowCountComponent' },
      ],
    };
  }

  ngOnInit(): void {
    this.rows$ = this.store.pipe(
      takeUntil(this.unsubscribeAll),
      select(PayrollSelectors.selectPayrollByNameUniq())
    );

    this.store
      .pipe(
        takeUntil(this.unsubscribeAll),
        select(CompaniesUISelectors.selectCurrentCompanyId)
      )
      .subscribe((companyId) => {
        this.store
          .pipe(
            takeUntil(this.unsubscribeAll),
            select(PayrollExtraSelectors.selectPayrollExtraByCompany(companyId))
          )
          .subscribe((extras) => {
            this.extraRows = extras;
            if (this.agGrid) {
              this.onGridReady(this.agGrid);
            }
          });
      });
  }

  ngOnChanges(simpleChange: SimpleChanges): void {
    if (simpleChange.years && simpleChange.years.currentValue) {
      if (this.agGrid) {
        this.onGridReady(this.agGrid);
      }
    }
  }

  // tslint:disable-next-line: ban-types
  numberParser(params): Number {
    return Number(params.newValue);
  }

  getColumnDefs(): any[] {
    return [
      {
        headerName: 'Nom Prénom',
        field: 'name',
        pinned: 'left',
        cellClass: 'nomprenom',
      },
      {
        headerName: 'Extras',
        children: [
          {
            headerName: 'Brut Prévu',
            width: 110,
            field: 'plannedRaw',
            editable: true,
            type: 'numericColumn',
            valueParser: this.numberParser,
            valueGetter: (params) => {
              return params.data && params.data.plannedRaw
                ? params.data.plannedRaw
                : 0;
            },
            cellRenderer: DecimalCellRenderer,
          },
        ],
      },
    ];
  }

  onGridReady(params): void {
    this.gridApi = params.api;
    this.columnDefs = this.getColumnDefs();

    this.extraRows.forEach((extra: PayrollExtraEntry) => {
      if (extra.name === '-- Nouveau --') {
        return false;
      }
      const defaultColumn: any = {
        headerName: extra.name,
        field: extra.name,
        editable: true,
        columnGroupShow: 'closed',
        valueGetter: ({ data }) => {
          const rules = data && data.extras ? data.extras : [];
          let value = null;

          _.forEach(rules, (r) => {
            if (r.payrollExtraKey === extra.name) {
              value = r.payrollExtraValue;
            }
          });

          return value;
        },
        cellClass: ['extra-column'],
      };

      if (extra.combobox) {
        Object.assign(defaultColumn, {
          cellEditorParams: {
            values: extra.choices ? extra.choices.split('|') : [],
          },
          cellEditor: 'agRichSelectCellEditor',
        });
      }

      if (extra.applicable) {
        this.columnDefs[1].children.push(defaultColumn);
      }
    });

    // Column start timeline
    let colCountPosition = 1;

    // Add timeline
    this.years.forEach((selectedYear) => {
      colCountPosition++;
      // Add flux to column
      this.columnDefs.push({
        headerName: selectedYear,
        children: [],
        cellRenderer: DecimalCellRenderer,
      });

      this.columnDefs[colCountPosition].children.push({
        headerName: 'Total',
        field: 'totalFlux' + selectedYear,
        aggFunc: 'sum',
        type: 'numericColumn',
        cellRenderer: DecimalCellRenderer,
        valueGetter: ({ data }) => {
          return data.totalFlux[selectedYear];
        },
        width: 100,
        columnGroupShow: 'open',
      });
      new Array(12).fill(0).forEach((d, index) => {
        const timelineKey = `${selectedYear}-${
          index < 9 ? '0' + (index + 1) : index + 1
        }`;
        this.columnDefs[colCountPosition].children.push({
          headerName: timelineKey,
          width: 100,
          columnGroupShow: 'closed',
          type: 'numericColumn',
          aggFunc: 'sum',
          cellRenderer: DecimalCellRenderer,
          valueGetter: ({ data }) => {
            const isItemOnTimeline = data.items.filter(
              // tslint:disable-next-line: no-shadowed-variable
              (d) => d.timeline === timelineKey
            );
            if (isItemOnTimeline.length > 0) {
              return isItemOnTimeline[0].fullRawChargesRecalculated;
            }
            return null;
          },
        });
      });
    });

    this.gridColumnApi = params.columnApi;
    this.gridApi.setQuickFilter(this._search);
    params.api.setColumnDefs(this.columnDefs);

    setTimeout(() => this.autoSizeAll(params));
  }

  public clearSearch(): void {
    this.gridApi.setQuickFilter('');
  }

  onFirstDataRendered(params): void {
    this.autoSizeAll(params);
  }

  private autoSizeAll(params): void {
    const allColumnIds = params.columnApi
      .getAllColumns()
      .map((column) => column.colId);
    setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
  }

  onCellValueChanged(event): void {
    const data = event.data;
    const input: PayrollRuleEntry = {} as PayrollRuleEntry;

    input.companyId = this.companyId;
    input.payrollExtraKey = event.column.colDef.field;
    input.payrollExtraValue = data[input.payrollExtraKey];
    input.payrollKey = data.matricule;
    input.axis = 'extras';

    if (input.payrollExtraValue) {
      if (event.oldValue) {
        input.key = null;
        this.store.dispatch(
          PayrollRuleActions.payrollRuleUpdateOneRequested({ input })
        );
      } else {
        this.store.dispatch(
          PayrollRuleActions.payrollRuleCreateRequested({ input })
        );
      }
    } else {
      const key = _.findKey(data.extras, {
        payrollExtraKey: input.payrollExtraKey,
        payrollExtraValue: event.oldValue,
      });

      this.store.dispatch(
        PayrollRuleActions.payrollRuleRemoveOneRequested({
          input: {
            companyId: input.companyId,
            key: data.extras[key].key,
          },
        })
      );
    }

    this.autoSizeAll(event);
    event.api.deselectAll();
  }

  onCellDoubleClicked(event): void {
    const data = {
      name: event.value,
      year: this.years,
    };

    if (event.node && event.colDef.field === 'name') {
      this.dialog.open(PayrollDetailComponent, {
        maxWidth: '95vw',
        maxHeight: '95vh',
        width: '85vw',
        height: '85vh',
        // panelClass: 'dialog-container-no-padding',
        data,
      });
    }
  }
}
